import { useRef } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
} from '@chakra-ui/react'

/**
 * ArchiveChildDialog — confirm-gated archive for a child profile (D-06).
 *
 * Mirrors ReconfirmPriceDialog's structure exactly: Chakra `AlertDialog
 * size="sm" isCentered` with `leastDestructiveRef` on the "Keep {name}" button
 * so the non-destructive action holds initial focus. Overlay-click and Escape
 * both resolve to "Keep {name}" (calls `onClose`).
 *
 * Copy per 14-UI-SPEC §Copywriting Contract:
 * - Header: "Archive {name}?"
 * - Body:   "{name} will be removed from your family list. Past bookings are kept."
 * - Buttons: "Keep {name}" (light, least-destructive) / "Archive" (#FF3B30 destructive).
 */

interface ArchiveChildDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading?: boolean
  name: string
}

const ArchiveChildDialog = ({ isOpen, onClose, onConfirm, isLoading, name }: ArchiveChildDialogProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null)

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose} leastDestructiveRef={cancelRef} size="sm" isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{`Archive ${name}?`}</AlertDialogHeader>
          <AlertDialogBody>
            <Text>{`${name} will be removed from your family list. Past bookings are kept.`}</Text>
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button ref={cancelRef} variant="light" onClick={onClose} minH={11}>
              {`Keep ${name}`}
            </Button>
            <Button variant="light" color="#FF3B30" borderColor="#FF3B30" onClick={onConfirm} isLoading={isLoading} minH={11}>
              Archive
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default ArchiveChildDialog
export type { ArchiveChildDialogProps }
